import { config } from './config'
import { Role } from './lib/auth/role'
import type { User } from './types/user'

export const paths = {
	home: '/',
	auth: {
		custom: {
			signIn: '/auth/custom/sign-in',
			signUp: '/auth/custom/sign-up',
			resetPassword: '/auth/custom/reset-password',
		},
		auth0: {
			callback: '/auth/auth0/callback',
			signIn: '/auth/auth0/sign-in',
			signUp: '/auth/auth0/sign-up',
			signOut: '/auth/auth0/sign-out',
			profile: '/auth/auth0/profile',
		},
		frontegg: {
			signIn: '/auth/frontegg/sign-in',
		},
		fronteggCustom: {
			signIn: '/auth/frontegg_custom/sign-in',
		},
	},
	dashboard: {
		overview: '/dashboard',
		health: '/dashboard/health',
		healthCatalog: '/dashboard/health/catalog',
		healthTab: (tab: 'overview' | 'notifications') =>
			tab === 'overview' ? '/dashboard/health' : `/dashboard/health?tab=${tab}`,
		settings: {
			account: '/dashboard/settings/account',
			notifications: '/dashboard/settings/notifications',
			team: '/dashboard/settings/team',
			support: '/dashboard/settings/support',
			cabinets: '/dashboard/settings/cabinets',
			api: '/dashboard/settings/integrations/api',
			mcp: '/dashboard/settings/integrations/mcp',
			integrations: '/dashboard/settings/integrations',
			quickbooks: '/dashboard/settings/integrations/quickbooks',
			canva: '/dashboard/settings/integrations/canva',
			googleBusinessProfile: '/dashboard/settings/integrations/google-business-profile',
			vdi: '/dashboard/settings/integrations/vdi',
			aiSearch: '/dashboard/settings/integrations/ai-search',
			revenueProtect: '/dashboard/settings/integrations/revenue-protect',
			guardian: '/dashboard/settings/integrations/guardian',
			shield: '/dashboard/settings/integrations/shield',
			interac: '/dashboard/settings/integrations/interac',
			assistant: '/dashboard/settings/integrations/assistant',
			footTraffic: '/dashboard/settings/integrations/foot-traffic',
			taxes: {
				home: '/dashboard/settings/taxes',
				onboarding: '/dashboard/settings/taxes/onboarding',
				regions: {
					list: '/dashboard/settings/taxes/regions',
					details: (regionId: number) => `/dashboard/settings/taxes/regions/${regionId}`,
				},
				groups: {
					list: '/dashboard/settings/taxes/groups',
					details: (groupId: number) => `/dashboard/settings/taxes/groups/${groupId}`,
				},
			},
		},
		promotions: {
			list: '/dashboard/promotions',
			create: '/dashboard/promotions/create',
			preview: (promotionId: number) => `/dashboard/promotions?preview_id=${promotionId}`,
			edit: (promotionId: number) => `/dashboard/promotions/${promotionId}`,
		},
		products: {
			list: '/dashboard/products',
			add: '/dashboard/products/add',
			pricing: '/dashboard/products/pricing',
			create: '/dashboard/products/create',
			preview: (productId: number) => `/dashboard/products?preview_id=${productId}`,
			details: (productId: number) => `/dashboard/products/${productId}`,
		},
		stores: {
			list: '/dashboard/stores',
			create: '/dashboard/stores/create',
			details: (storeId: number) => `/dashboard/stores/${storeId}`,
			topDisplayPreview: (storeId: number) => `/dashboard/stores/${storeId}/top-display-preview`,
			edit: (storeId: number) => `/dashboard/stores/${storeId}/edit`,
			cabinets: {
				details: (storeId: number, cabinetId: string, isPending = false) =>
					`/dashboard/stores/${storeId}/cabinets/${cabinetId}${isPending ? '?type=pending' : ''}`,
				create: (storeId: number) => `/dashboard/stores/${storeId}/cabinets/create`,
				edit: (storeId: number, cabinetId: string) => `/dashboard/stores/${storeId}/cabinets/${cabinetId}/edit`,
				pricing: {
					manage: (storeId: number, cabinetId: string) => `/dashboard/stores/${storeId}/cabinets/${cabinetId}/pricing`,
					missingPrices: (storeId: number, cabinetId: string) =>
						`/dashboard/stores/${storeId}/cabinets/${cabinetId}/pricing?missing_prices=true`,
				},
				configurePriceTags: (storeId: number, cabinetId: string) =>
					`/dashboard/stores/${storeId}/cabinets/${cabinetId}/assign_price_tags`,
				planogram: {
					create: (storeId: number, cabinetId: string) =>
						`/dashboard/stores/${storeId}/cabinets/${cabinetId}/planogram/create`,
					edit: (storeId: number, cabinetId: string, planogramId: number, isNew = false) =>
						`/dashboard/stores/${storeId}/cabinets/${cabinetId}/planogram/${planogramId}${isNew ? '/new' : ''}`,
				},
			},
		},
		restocks: {
			list: '/dashboard/restocks',
			new: '/dashboard/restocks/new',
			chooseCabinet: (storeId: number) => `/dashboard/restocks/new/${storeId}/cabinets`,
			restockCabinet: (storeId: number, cabinetId: string, restockId: number) =>
				`/dashboard/restocks/new/${storeId}/cabinets/${cabinetId}/${restockId}`,
			restockCabinetSummary: (storeId: number, cabinetId: string, restockId: number) =>
				`/dashboard/restocks/new/${storeId}/cabinets/${cabinetId}/${restockId}/summary`,
			details: (restockId: number) => `/dashboard/restocks/${restockId}`,
		},
		planograms: {
			list: '/dashboard/planograms',
			preview: (planogramId: number) => `/dashboard/planograms?preview_id=${planogramId}`,
			details: (planogramId: number) => `/dashboard/planograms/${planogramId}`,
		},
		marketing: {
			list: '/dashboard/marketing',
			canva: '/dashboard/marketing/canva',
			folders: '/dashboard/marketing/folders',
			folder: (folderId: string) => `/dashboard/marketing/folders/${folderId}`,
			preview: (assetId: string) => `/dashboard/marketing?preview_id=${assetId}`,
			edit: (assetId: string) => `/dashboard/marketing/${assetId}/edit`,
			delete: (assetId: string) => `/dashboard/marketing/${assetId}/delete`,
		},
		transactions: {
			list: '/dashboard/transactions',
			preview: (transactionID: number) => `/dashboard/transactions?preview_id=${transactionID}`,
		},
		finances: {
			list: '/dashboard/finances',
			preview: (payoutID: number) => `/dashboard/finances?preview_id=${payoutID}`,
		},
		copilot: {
			list: '/dashboard/copilot',
			thread: (copilotID: number) => `/dashboard/copilot?thread_id=${copilotID}`,
		},
		inventory: {
			list: '/dashboard/inventory',
		},
		marketInsights: {
			overview: '/dashboard/market-insights',
			recommendations: '/dashboard/market-insights/recommendations',
			products: '/dashboard/market-insights/products',
			stores: '/dashboard/market-insights/stores',
			pricing: '/dashboard/market-insights/pricing',
			planograms: '/dashboard/market-insights/planograms',
		},
		locationInsights: {
			overview: '/dashboard/location-insights',
			traffic: '/dashboard/location-insights/traffic',
			audience: '/dashboard/location-insights/audience',
		},
		help: {
			typeform: (formId: string) => `/dashboard/help/typeform/${formId}`,
		},
		analytics: {
			collections: '/dashboard/analytics',
			inventory: '/dashboard/analytics/inventory',
			sales: '/dashboard/analytics/sales',
			dailySales: '/dashboard/analytics/daily-sales',
			taxReport: '/dashboard/analytics/tax-report',
			earnings: '/dashboard/analytics/earnings',
			restockAudit: '/dashboard/analytics/restock-audit',
			dashboard: (dashboardId: string) => `/dashboard/analytics/${dashboardId}`,
		},
	},
	learn: 'https://learn.micromart.com',
	learnMarketingVideos: 'https://learn.micromart.com/how-to-create-a-video-for-your-smart-store',
	learnFinances: 'https://learn.micromart.com/operator-payouts',
	learnTaxSetup: 'https://learn.micromart.com/tax-setup',
	learnPlanogramTemplates: 'https://learn.micromart.com/planogram-templates',
	learnHealth: 'https://learn.micromart.com/cabinet-health',
	statusPage: 'https://status.micromart.com',
	// Internal-only Super (classic) pages
	super: {
		visionOrder: (fridgeOpenId: number) => `${config.fridge.superUrl}/vision-orders/${fridgeOpenId}`,
	},
	notAuthorized: '/errors/not-authorized',
	notFound: '/errors/not-found',
	internalServerError: '/errors/internal-server-error',
	siteUpdated: '/errors/site-updated',
} as const

export const getDefaultPathForUser = (user: User | null) => {
	return user?.role === Role.Restocker ? paths.dashboard.restocks.list : paths.dashboard.overview
}
